'use client';

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams, usePathname } from 'next/navigation';
import {
  Box,
  IconButton,
  TooltipContent,
  useActiveWindowBreakpoint,
  useTooltip,
} from '@frameio/vapor';
import { IconX } from '@frameio/vapor-icons';
import {
  AppSheetContext,
  useVersionStackModalContext,
} from '../../../contexts';
import { useTrackUnsafe } from '../../../segment/hooks';
import { tooltipStore } from '../../stores/tooltipStore';
import type {
  TooltipAction,
  TooltipState,
  TooltipTarget,
} from '../../stores/tooltipStore.types';
import TEXT from './generated/TEXT/OnboardingTooltipContainer';
import { OnboardingTooltipBody } from './OnboardingTooltipBody';
import styles from './OnboardingTooltipContainer.module.css';
import { useHighlightOverlay } from './useHighlightOverlay';
import { useTargetVisibility } from './useTargetVisibility';

TEXT.DEFINE({
  /** Label for the back button in multi-step tooltips */
  back: 'Back',
  /** Label for the next button in multi-step tooltips */
  next: 'Next',
  /** Label for the done button in multi-step tooltips */
  done: 'Done',
  /** Label for the close button in tooltips */
  close: 'Close',
});

const OBSERVER_DEBOUNCE_MS = 100;
const CANDIDATE_EXPIRY_TIMEOUT_MS = 3000;
/** Below AccountSwitcherMenu (1501) so nav overlays stay on top */
const ONBOARDING_TOOLTIP_Z_INDEX = 1500;

/**
 * Utility function to resolve a tooltip target to an actual DOM element
 */
function resolveTarget(target?: TooltipTarget): HTMLElement | null {
  if (!target) return null;

  try {
    return document.querySelector(target);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Invalid CSS selector for onboarding tooltip: "${target}"`);
    return null;
  }
}

/**
 * Individual tooltip renderer
 */
function OnboardingTooltipRenderer({
  id,
  config,
  isCandidate = false,
  isMultiStep = false,
  stepInfo,
}: {
  id: string;
  config: TooltipState['config'];
  isCandidate?: boolean;
  isMultiStep?: boolean;
  stepInfo?: { current: number; total: number; groupId: string };
}) {
  const track = useTrackUnsafe();
  const params = useParams<{ assetId?: string }>();
  const { toggleVersionStackModal } = useVersionStackModalContext();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [isShowingNow, setIsShowingNow] = useState<boolean>(() => !isCandidate);
  const showDelayTimeoutRef = useRef<number | null>(null);
  const breakpoint = useActiveWindowBreakpoint();

  // Vapor Tooltip hook
  const tooltipResult = useTooltip({ disabled: false });

  // Keep a stable reference to the targetRef so our effect deps can be minimal
  const targetRefRef = useRef(tooltipResult.targetRef);
  useEffect(() => {
    targetRefRef.current = tooltipResult.targetRef;
  }, [tooltipResult.targetRef]);

  const bindElement = useCallback(
    (element: HTMLElement) => {
      if (targetRefRef.current) {
        targetRefRef.current.current = element;
      }
      if (isCandidate) {
        // If we're ready to show a new tooltip that has a delay, set a timeout to show it
        const delayMs = config.delayMs ?? 0;
        if (showDelayTimeoutRef.current != null) {
          window.clearTimeout(showDelayTimeoutRef.current);
          showDelayTimeoutRef.current = null;
        }
        if (delayMs > 0) {
          showDelayTimeoutRef.current = window.setTimeout(() => {
            showDelayTimeoutRef.current = null;
            setIsShowingNow(true);
            if (tooltipStore.confirmOpen(id)) {
              track('onboarding-tooltip-displayed', {
                tooltip_id: id,
                tooltip_variant: config.variant,
              });
            }
          }, delayMs);
        } else {
          setIsShowingNow(true);
          if (tooltipStore.confirmOpen(id)) {
            track('onboarding-tooltip-displayed', {
              tooltip_id: id,
              tooltip_variant: config.variant,
            });
          }
        }
      }
    },
    [config, id, isCandidate, track]
  );
  // Find the target element and bind it to the tooltip
  useEffect(() => {
    let observer: MutationObserver | null = null;

    // First, to avoid redundant queries on the document, if we already have a target element and it still
    // matches the selector and is in the DOM, rebind the latest ref and confirm open (if applicable).
    let bound = false;
    if (targetElement) {
      let isSameTarget = false;
      try {
        isSameTarget = !!config.target && targetElement.matches(config.target);
      } catch {
        isSameTarget = false;
      }
      if (isSameTarget && document.contains(targetElement)) {
        bindElement(targetElement);
        bound = true;
      }
    }

    // If not already bound, try to resolve and bind now
    const tryBind = () => {
      const element = resolveTarget(config.target);
      if (element) {
        setTargetElement(element);
        bindElement(element);
        return true;
      }
      return false;
    };

    if (!bound) {
      bound = tryBind();
      if (!bound) {
        // If element not found, set up a MutationObserver to watch for it
        let debounceTimeoutId: number | null = null;

        const scheduleTryBind = () => {
          if (debounceTimeoutId != null) return;
          debounceTimeoutId = window.setTimeout(() => {
            debounceTimeoutId = null;
            if (tryBind() && observer) {
              observer.disconnect();
              observer = null;
            }
          }, OBSERVER_DEBOUNCE_MS);
        };

        observer = new MutationObserver(() => {
          scheduleTryBind();
        });

        // Watch for added nodes in the document
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });

        // Cleanup: disconnect observer and clear any pending debounce
        return () => {
          if (observer) observer.disconnect();
          if (debounceTimeoutId != null) {
            window.clearTimeout(debounceTimeoutId);
            debounceTimeoutId = null;
          }
        };
      }
    }

    // Cleanup observer on unmount
    return () => {
      if (observer) observer.disconnect();
    };
  }, [bindElement, config.target, id, isCandidate, targetElement]);

  // If we are the active candidate but cannot find a target within a timeout, release and retry later
  useEffect(() => {
    if (!isCandidate) return undefined;
    if (targetElement) return undefined;
    const timeout = setTimeout(() => {
      // Still no target; release this candidate so the queue can progress
      tooltipStore.releaseActive(id, { retry: true });
    }, CANDIDATE_EXPIRY_TIMEOUT_MS);
    return () => clearTimeout(timeout);
  }, [id, isCandidate, targetElement]);

  // The target is a foreign element found by selector, so the tooltip's
  // description link has to be applied imperatively. Add and remove only our own
  // id so a concurrent writer's value survives.
  const describedById = tooltipResult.bindTarget['aria-describedby'];
  useEffect(() => {
    if (!targetElement || !isShowingNow || !describedById) return undefined;

    const read = () =>
      new Set(
        targetElement
          .getAttribute('aria-describedby')
          ?.split(/\s+/)
          .filter(Boolean)
      );
    const write = (ids: Set<string>) => {
      if (ids.size > 0) {
        targetElement.setAttribute('aria-describedby', [...ids].join(' '));
      } else {
        targetElement.removeAttribute('aria-describedby');
      }
    };

    const ids = read();
    ids.add(describedById);
    write(ids);

    return () => {
      const remaining = read();
      remaining.delete(describedById);
      write(remaining);
    };
  }, [describedById, isShowingNow, targetElement]);

  // Cleanup delay timeout on unmount or id/isCandidate change
  useEffect(() => {
    return () => {
      if (showDelayTimeoutRef.current != null) {
        window.clearTimeout(showDelayTimeoutRef.current);
        showDelayTimeoutRef.current = null;
      }
    };
  }, [id, isCandidate]);

  // Shared by the highlight and tooltip so they fade together.
  const { isVisible: isTargetVisible, isOccluded: isTargetOccluded } =
    useTargetVisibility(targetElement);

  useEffect(() => {
    if (
      !targetElement ||
      !isShowingNow ||
      !isTargetVisible ||
      !config.autoDismissMs
    ) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      tooltipStore.dismissTooltip(id);
    }, config.autoDismissMs);
    return () => clearTimeout(timeout);
  }, [config.autoDismissMs, id, isShowingNow, isTargetVisible, targetElement]);

  // If a modal buries the target, yield so a tooltip inside that modal can show
  // — but only when another is queued to take over, else it stays mounted (and
  // hidden) and reappears on its own once uncovered.
  useEffect(() => {
    if (isTargetOccluded && tooltipStore.hasSelectablePendingTooltip()) {
      tooltipStore.releaseActive(id, { retry: true });
    }
  }, [isTargetOccluded, id]);

  // Highlight overlay follows the target element
  useHighlightOverlay({
    targetElement,
    active: isShowingNow && config.showHighlight && targetElement !== null,
    className: styles.highlightOverlay,
    visibleClassName: styles.highlightOverlayVisible,
    isTargetVisible,
  });

  const handleDismiss = (e?: React.MouseEvent | MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    tooltipStore.dismissTooltip(id, 'X');
  };

  const handleAction = (action: TooltipAction) => {
    if (action.kind === 'link') {
      window.open(
        action.href,
        action.openInNewTab ? '_blank' : '_self',
        'noopener,noreferrer'
      );
    } else if (action.kind === 'command') {
      if (action.command === 'dismiss') {
        tooltipStore.dismissTooltip(id);
      } else if (action.command === 'next' && stepInfo) {
        tooltipStore.nextStep(stepInfo.groupId, id);
      } else if (action.command === 'previous' && stepInfo) {
        tooltipStore.previousStep(stepInfo.groupId, id);
      } else if (action.command === 'open-version-history') {
        if (params?.assetId) {
          toggleVersionStackModal(params.assetId);
        }
        tooltipStore.dismissTooltip(id);
      }
    }
  };

  // Generate navigation actions for multi-step groups
  const navigationActions: TooltipAction[] =
    isMultiStep && stepInfo
      ? [
          ...(stepInfo.current > 0
            ? [
                {
                  kind: 'command' as const,
                  label: TEXT.back,
                  command: 'previous' as const,
                },
              ]
            : []),
          ...(stepInfo.current < stepInfo.total - 1
            ? [
                {
                  kind: 'command' as const,
                  label: TEXT.next,
                  command: 'next' as const,
                },
              ]
            : []),
          ...(stepInfo.current === stepInfo.total - 1
            ? [
                {
                  kind: 'command' as const,
                  label: TEXT.done,
                  command: 'dismiss' as const,
                },
              ]
            : []),
        ]
      : [];

  // Combine step-specific action with navigation actions
  const allActions: TooltipAction[] = [
    ...(config.action ? [config.action] : []),
    ...navigationActions,
  ];

  const tooltipContent = (
    <Box className={styles.tooltipContainer} data-react-aria-top-layer="true">
      <div className={styles.closeButtonWrapper}>
        <IconButton
          color="neutral"
          icon={<IconX size={24} />}
          label={TEXT.close}
          onClick={handleDismiss}
          onMouseDown={handleDismiss}
          size="small"
        />
      </div>

      <OnboardingTooltipBody
        actionAlignment={config.actionAlignment}
        actions={allActions.length > 0 ? allActions : undefined}
        description={config.description}
        icon={config.icon}
        link={config.link}
        media={config.media}
        onAction={handleAction}
        showBetaChip={config.showBetaChip}
        stepInfo={stepInfo}
        title={config.title}
      />
    </Box>
  );

  if (!targetElement) {
    return null;
  }

  // Determine placement based on breakpoint if placementByBreakpoint is provided
  const getPlacement = ():
    | 'top'
    | 'bottom'
    | 'left'
    | 'left-start'
    | 'right'
    | 'right-start' => {
    if (config.placementByBreakpoint) {
      const isMobile = breakpoint === 'base';
      if (isMobile && config.placementByBreakpoint.mobile) {
        return config.placementByBreakpoint.mobile;
      }
      if (!isMobile && config.placementByBreakpoint.desktop) {
        return config.placementByBreakpoint.desktop;
      }
    }
    return config.placement || 'top';
  };

  return (
    <TooltipContent
      ref={tooltipResult.bindContent.ref}
      animateOnMount={tooltipResult.bindContent.animateOnMount}
      animateOnUnmount={tooltipResult.bindContent.animateOnUnmount}
      data-onboarding="true"
      id={tooltipResult.bindContent.id}
      isShowing={isShowingNow && isTargetVisible}
      maxWidth={config.maxWidth}
      placement={getPlacement()}
      role={tooltipResult.bindContent.role}
      shouldUsePortal={true}
      shouldUsePositioner={tooltipResult.bindContent.shouldUsePositioner}
      target={tooltipResult.bindContent.target}
      title={config.presentation === 'compact' ? config.title : tooltipContent}
      zIndex={ONBOARDING_TOOLTIP_Z_INDEX}
    />
  );
}

// Global container that renders a single onboarding tooltip at a time
export function OnboardingTooltipContainer() {
  const { id: sheetId } = useContext(AppSheetContext);
  const pathname = usePathname();
  const track = useTrackUnsafe();
  const breakpoint = useActiveWindowBreakpoint();
  const isMobile = breakpoint === 'base';
  const isSheetOpen = sheetId != null;
  const [openTooltip, setOpenTooltip] = useState<TooltipState | null>(() =>
    tooltipStore.getOpenTooltipForRoute(pathname)
  );
  const [activeCandidate, setActiveCandidate] = useState<TooltipState | null>(
    () => {
      const active = tooltipStore.getActiveTooltip();
      // For multi-step groups, use the current step
      if (active?.config.multiStepGroupId) {
        return tooltipStore.getCurrentStepTooltip();
      }
      return active;
    }
  );
  const prevOpenIdRef = useRef<string | null>(openTooltip?.id ?? null);
  const lastProcessedPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const unsubscribe = tooltipStore.subscribe(() => {
      const nextOpen = tooltipStore.getOpenTooltipForRoute(pathname);
      const prevOpenId = prevOpenIdRef.current;
      if (prevOpenId && prevOpenId !== nextOpen?.id) {
        const prevState = tooltipStore
          .getAllTooltips()
          .find((t) => t.id === prevOpenId);
        if (prevState?.isDismissed) {
          track('onboarding-tooltip-dismissed', {
            tooltip_id: prevOpenId,
            dismissal_method: prevState.lastDismissalMethod ?? 'Completed',
            tooltip_variant: prevState.config.variant,
          });
        }
      }
      prevOpenIdRef.current = nextOpen?.id ?? null;

      setOpenTooltip(nextOpen);
      const active = tooltipStore.getActiveTooltip();
      // For multi-step groups, use the current step
      if (active?.config.multiStepGroupId) {
        setActiveCandidate(tooltipStore.getCurrentStepTooltip());
      } else {
        setActiveCandidate(active);
      }
    });

    // Open ready tooltips when conditions are met on route changes
    const lastPath = lastProcessedPathnameRef.current;
    const isFirstRun = lastPath == null;
    const pathnameChanged = lastPath !== null && lastPath !== pathname;

    if (isFirstRun) {
      tooltipStore.openReadyTooltipsForRoute(pathname);
      lastProcessedPathnameRef.current = pathname;
    } else if (pathnameChanged) {
      // On route change, release any active tooltip so it reopens via candidate flow
      // This ensures displayCount increments on navigation as well as reloads
      const active = tooltipStore.getActiveTooltip();
      if (active?.isOpen) {
        tooltipStore.releaseActive(active.id, { retry: false });
      }
      tooltipStore.openReadyTooltipsForRoute(pathname);
      lastProcessedPathnameRef.current = pathname;
    }

    return () => {
      unsubscribe();
    };
  }, [pathname, track]);

  if (isSheetOpen) return null;

  const currentTooltip = openTooltip || activeCandidate;
  if (!currentTooltip) return null;

  // Check if tooltip should be disabled on mobile
  if (isMobile && currentTooltip.config.disableOnMobile) {
    return null;
  }

  const stepInfo = tooltipStore.getStepInfo(currentTooltip.id);
  const isMultiStep = Boolean(currentTooltip.config.multiStepGroupId);

  return (
    <OnboardingTooltipRenderer
      key={currentTooltip.id}
      config={{ ...currentTooltip.config, ...currentTooltip.contentOverrides }}
      id={currentTooltip.id}
      isCandidate={!openTooltip}
      isMultiStep={isMultiStep}
      stepInfo={stepInfo ?? undefined}
    />
  );
}
